import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { Badge, EmptyRow, LEVEL_LABELS, PanelTitle, StatCard, Table, fmtDate, fmtDateTime, shortHash } from "../office/ui";
import { RankBadge } from "../office/RanksCard";
import { KIND_LABELS, kindDetail } from "../office/MonthlyPanel";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
const thisPeriod = () => new Date().toISOString().slice(0, 7);

export const MonthlyCutsPanel = () => {
  const [period, setPeriod] = useState(thisPeriod());
  const [preview, setPreview] = useState(null);
  const [comm, setComm] = useState(null);
  const [status, setStatus] = useState("pendiente");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([api.get("/admin/monthly/preview", { params: { period } }), api.get("/admin/monthly/commissions", { params: { period, status } })]);
      setPreview(p.data);
      setComm(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [period, status]);
  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(period)) load();
  }, [load, period]);

  const cut = async (definitivo) => {
    const force = definitivo && !!preview?.definitivo;
    if (definitivo && !window.confirm(force ? "Este mes ya tiene corte definitivo. ¿Recalcular? Se conservan las comisiones ya pagadas." : `¿Hacer el CORTE DEFINITIVO de ${period}? Se generarán las comisiones mensuales a pagar y se reportarán a toda la red.`)) return;
    setBusy(definitivo ? "def" : "par");
    try {
      const { data } = await api.post("/admin/monthly/cut", { period, definitivo, force });
      toast.success(definitivo ? `Corte definitivo aplicado: ${fmt(data.cut.total)} en ${data.cut.beneficiaries} beneficiarios` : "Corte parcial actualizado: la red ya ve su proyección");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const pay = async (c) => {
    const tx = window.prompt(`Pagar ${fmt(c.amount)} a ${c.beneficiary_name} (${c.beneficiary_id}).\nHash de la transacción (opcional):`, "");
    if (tx === null) return;
    try {
      await api.post(`/admin/monthly/commissions/${c.id}/pay`, { tx_hash: tx });
      toast.success("Comisión mensual marcada como pagada; el distribuidor ya la ve como cobrada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="monthly-cuts-panel">
      <PanelTitle title="Cortes mensuales · Ganancias Mensuales" text="Compras de códigos pagadas (carrito) + ventas a clientes del mes. Ventas personales escalonadas ($5 → $7 por código, 1er nivel), $1 por código a 5 niveles y estímulos por rango: ORO 2% (11-30 ventas), DIAMANTE 3% (31-50), CORONA 4% (51+) de la venta mensual global, repartidos entre quienes califican. El corte parcial solo muestra la proyección a la red; el corte definitivo genera las comisiones a pagar.">
        <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-11 w-48 bg-white" data-testid="cuts-period" />
      </PanelTitle>
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard dark label="Códigos del mes" value={preview ? preview.units_total : "…"} hint={preview ? `${preview.purchases} pedidos · ${preview.client_sales} ventas a clientes · venta global ${fmt(preview.global_sales)}` : ""} testId="cuts-units" />
        <StatCard label="Ventas personales" value={preview ? fmt(preview.total_personal) : "…"} testId="cuts-personal" />
        <StatCard label="Ganancias en red" value={preview ? fmt(preview.total_red) : "…"} testId="cuts-red" />
        <StatCard label="Estímulos por rango" value={preview ? fmt(preview.total_ranks) : "…"} hint={preview ? preview.ranks.map((r) => `${r.rank} ${r.qualifiers}`).join(" · ") : ""} testId="cuts-ranks" />
        <StatCard label="Total a repartir" value={preview ? fmt(preview.total) : "…"} testId="cuts-total" />
        <StatCard label="Estado del mes" value={preview?.definitivo ? "Cerrado" : "Abierto"} hint={preview?.definitivo ? `Corte ${fmtDateTime(preview.definitivo.created_at)}` : "Sin corte definitivo"} testId="cuts-state" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => cut(false)} disabled={!!busy} className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-3 text-sm font-semibold hover:bg-paper disabled:opacity-60" data-testid="cut-partial">{busy === "par" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Corte parcial (proyección)</button>
        <button onClick={() => cut(true)} disabled={!!busy} className="btn-leaf !px-5 !py-3 !text-sm disabled:opacity-60" data-testid="cut-definitive">{busy === "def" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />} {preview?.definitivo ? "Recalcular corte definitivo" : "Corte definitivo del mes"}</button>
      </div>

      {preview && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="cuts-rank-pools">
          {preview.ranks.map((r) => (
            <div key={r.rank} className="rounded-2xl border border-ink/10 bg-white p-4" data-testid={`cuts-pool-${r.rank.toLowerCase()}`}>
              <div className="flex items-center justify-between"><RankBadge rank={r.rank} /><span className="text-sm text-ink/60">{Math.round(r.pct * 100)}% · {r.max_units ? `${r.min_units}-${r.max_units}` : `${r.min_units}+`} ventas</span></div>
              <p className="mt-2 font-display text-2xl font-semibold">{fmt(r.pool)}</p>
              <p className="text-xs text-ink/60">{r.qualifiers ? `${r.qualifiers} calificado(s) · ${fmt(r.share)} cada uno` : "Sin calificados: el pool no se reparte"}</p>
            </div>
          ))}
        </div>
      )}

      <h3 className="mt-8 font-display text-xl font-semibold">Proyección por distribuidor</h3>
      <Table headers={["Distribuidor", "Ventas / pagos activos", "Ventas personales", "Red por nivel (códigos)", "Ganancia red", "Rango", "Estímulo", "Total"]} testId="cuts-preview-table" minWidth="min-w-[1100px]">
        {!preview && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {preview && !preview.lines.length && <EmptyRow cols={8} text="Sin compras ni ventas confirmadas en este periodo." testId="cuts-empty" />}
        {preview?.lines.map((l, i) => (
          <tr key={l.beneficiary_id} className="border-b border-ink/5 last:border-0" data-testid={`cuts-line-${i}`}>
            <td className="px-4 py-3"><p className="font-semibold">{l.beneficiary_name}</p><p className="font-mono text-xs text-ink/60">{l.beneficiary_id}</p></td>
            <td className="px-4 py-3">{l.personal_units}</td>
            <td className="px-4 py-3 font-semibold">{fmt(l.personal_amount)}</td>
            <td className="px-4 py-3 text-xs">{Object.entries(l.red_units).map(([lv, u]) => `${LEVEL_LABELS[lv]}: ${u}`).join(" · ")}</td>
            <td className="px-4 py-3 font-semibold">{fmt(l.red_amount)}</td>
            <td className="px-4 py-3"><RankBadge rank={l.rank} testId={`cuts-rank-${i}`} /></td>
            <td className="px-4 py-3 font-semibold" data-testid={`cuts-bonus-${i}`}>{fmt(l.rank_bonus)}</td>
            <td className="px-4 py-3 font-display text-base font-semibold">{fmt(l.total)}</td>
          </tr>
        ))}
      </Table>

      <div className="mt-10 flex flex-wrap items-center gap-2">
        <h3 className="mr-2 font-display text-xl font-semibold">Comisiones mensuales {period}</h3>
        {[["pendiente", "Pendientes de pagar"], ["pagada", "Comisiones pagadas"], ["", "Todas"]].map(([v, l]) => <button key={v} onClick={() => setStatus(v)} className={`rounded-full px-3.5 py-2 text-xs font-semibold ${status === v ? "bg-ink text-white" : "border border-ink/15 bg-white hover:bg-paper"}`} data-testid={`mc-filter-${v || "all"}`}>{l}</button>)}
        {comm && <span className="ml-auto text-sm text-ink/70">Pagado {fmt(comm.paid)} · Pendiente {fmt(comm.pending)}</span>}
      </div>
      <Table headers={["Distribuidor", "Concepto", "Monto", "Wallet", "Estado", "Pagada el", "Hash", ""]} testId="mc-table" minWidth="min-w-[960px]">
        {!comm && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>}
        {comm && !comm.items.length && <EmptyRow cols={8} text="No hay comisiones en esta vista. Se generan con el corte definitivo." testId="mc-empty" />}
        {comm?.items.map((c, i) => (
          <tr key={c.id} className="border-b border-ink/5 last:border-0" data-testid={`mc-row-${i}`}>
            <td className="px-4 py-3"><p className="font-semibold">{c.beneficiary_name}</p><p className="font-mono text-xs text-ink/60">{c.beneficiary_id}</p></td>
            <td className="px-4 py-3"><p className="font-semibold">{KIND_LABELS[c.kind]} {c.kind === "rango" && <RankBadge rank={c.detail.rank} />}</p><p className="text-xs text-ink/60">{kindDetail(c)}</p></td>
            <td className="px-4 py-3 font-display text-base font-semibold">{fmt(c.amount)}</td>
            <td className="px-4 py-3 font-mono text-[11px]">{c.beneficiary_wallet ? shortHash(c.beneficiary_wallet) : <span className="text-coral">sin wallet</span>}</td>
            <td className="px-4 py-3"><Badge status={c.status} label={c.status === "pagada" ? "Pagada" : "Pendiente"} /></td>
            <td className="px-4 py-3 text-ink/70">{fmtDate(c.paid_at)}</td>
            <td className="px-4 py-3 font-mono text-xs">{shortHash(c.paid_tx_hash)}</td>
            <td className="px-4 py-3">{c.status === "pendiente" && <button onClick={() => pay(c)} className="rounded-full bg-ink px-3.5 py-2 text-xs font-semibold text-white" data-testid={`mc-pay-${c.id}`}>Marcar pagada</button>}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
